import React from 'react';
import _isEmpty from 'lodash/isEmpty';
import { BarLoader } from 'react-spinners';

import {brazeService, messageService, sectionService} from '../../services';
import campaignCatalog from '../../services/campaign-catalog-service';
import i18n from './messages.json';
import { _buildRootTopic } from '../../utils/common';
import { isFutureScheduled } from '../../utils/schedule-helper';

export const NotificationContext = React.createContext();

export function withContext(Component) {
  return function ComponentWithContext(props) {
    return (
      <NotificationContext.Consumer>
        {context => <Component {...props} context={context} />}
      </NotificationContext.Consumer>
    );
  };
}

const DEFAULT_MESSAGE = {
  title: {
    value: '',
    isValid: false,
    error: i18n.ink.notification.title.required,
  },
  body: {
    value: '',
    isValid: false,
    error: i18n.ink.notification.body.required,
  },
  targeting: {
    targetType: {},
    isValid: false,
    error: i18n.ink.notification.targeting.required,
  },
  directUrl: {
    value: '',
  },
  imageUrl: {
    value: '',
  },
  isIncludeImage: false,
  article: null,
  campaignIds: [],
};

export default class ContextProvider extends React.Component {

  constructor(props) {
    super(props);

    this.state = {
      updateState: this.updateState.bind(this),

      rootTopic: null,
      sectionList: [],
      topics: [],
      campaigns: [],
      campaignSizes: {},
      isCampaignsLoading: false,
      loadCampaigns: this.loadCampaigns.bind(this),
      currentMessage: DEFAULT_MESSAGE,
      messages: [],
      errorMessages: [],
      scheduledMessages: [],
      getScheduledMessages: this.getScheduledMessages.bind(this),
      saveScheduledMessage: this.saveScheduledMessage.bind(this),
      cancelScheduledMessage: this.cancelScheduledMessage.bind(this),

      isLoading: true,
      // pagination attrs
      activePage: 1,
      itemsCountPerPage: 25,
      pageRangeDisplayed: 5,
      totoalMessagesCount: 0,
      getMessages: this.getMessages.bind(this),
      // manage device tokens
      tokens: [],
      deleteToken: this.deleteToken.bind(this),
      setStateAsync: this.setStateAsync.bind(this)
    };
  }

  async componentDidMount() {
    // Fire-and-forget — preload Braze campaigns in the background so the
    // picker is ready by the time the user opens Compose / Edit. Runs in
    // parallel with the messages fetch; never blocks the page load.
    this.loadCampaigns();

    let sectionList = await sectionService.getSectionList();
    const rootTopic = _buildRootTopic(sectionList) || {};

    const {docs, total} = await messageService.getMessages({
      page: 1,
      paginate: this.state.itemsCountPerPage,
      isAddTimestamp: true,
      excludeFutureScheduled: true,
    });

    const failureMessages = await messageService.getErrorMessages();
    const scheduledMessages = await this._fetchScheduled();

    this.setState({
      rootTopic,
      sectionList,
      activePage: 1,
      messages: docs,
      errorMessages: failureMessages.docs,
      scheduledMessages,
      isLoading: false,
      totalMessagesCount: total,
    });
  }

  // Fetch status=scheduled messages and keep only the still-pending (future)
  // ones — past-due scheduled pushes have (best-effort) been sent by Braze and
  // belong in the Activity Log, not the Scheduled Pushes list.
  async _fetchScheduled() {
    try {
      const { docs } = await messageService.getScheduledMessages();

      return (docs || []).filter((m) => isFutureScheduled(m));
    } catch (e) {
      console.error('Failed to load scheduled pushes', e);

      return [];
    }
  }

  async getScheduledMessages() {
    const scheduledMessages = await this._fetchScheduled();

    this.setState({ scheduledMessages });
  }

  // Persist a scheduled push (create when new, update when editing) and refresh
  // the Scheduled Pushes list. Returns the saved message.
  async saveScheduledMessage(data) {
    const saved = data.id
      ? await messageService.updateMessage(data.id, data)
      : await messageService.createMessage(data);

    await this.getScheduledMessages();

    return saved;
  }

  // Cancel a scheduled push: delete each per-campaign Braze schedule, then delete
  // the persisted message, then refresh the list. If ANY Braze delete fails we
  // throw *before* deleting the record — otherwise we'd orphan a live Braze
  // schedule the UI could no longer cancel while falsely reporting success. The
  // caller keeps the row (and shows an error toast) so the user can retry.
  async cancelScheduledMessage(message) {
    const scheduleIds = (message.brazeScheduleIds || []).filter((entry) => entry && entry.scheduleId);

    const settled = await Promise.allSettled(
      scheduleIds.map((entry) =>
        brazeService.deleteScheduledCampaign({
          campaign_id: entry.campaignId,
          schedule_id: entry.scheduleId,
        })
      )
    );

    const failed = settled.filter((r) => r.status === 'rejected');

    if (failed.length > 0) {
      console.error('Failed to cancel Braze schedule(s)', failed.map((r) => r.reason));

      throw new Error(`Could not cancel ${failed.length} of ${scheduleIds.length} Braze schedule(s)`);
    }

    await messageService.deleteMessage(message.id);
    await this.getScheduledMessages();
  }

  loadCampaigns() {
    if (this.state.isCampaignsLoading || this._campaignsLoaded) return;

    this.setState({ isCampaignsLoading: true });

    // Fetched fresh from Braze on every load — no caching (WWW-cache-revert).
    // Sizes (per-campaign reachable-users estimate) are fetched right after,
    // keyed by every campaign id, for the picker + compose chips.
    campaignCatalog.loadCampaigns()
      .then((campaigns) => {
        this._campaignsLoaded = true;
        this.setState({ campaigns: campaigns || [], isCampaignsLoading: false });

        return campaignCatalog.loadSizes((campaigns || []).map((c) => c.id));
      })
      .then((campaignSizes) => {
        if (campaignSizes) this.setState({ campaignSizes });
      })
      .catch((e) => {
        console.error('Failed to load Braze campaigns', e);
        this.setState({ isCampaignsLoading: false });
      });
  }

  updateState(state){
    this.setState(state);
  }

  setStateAsync(state) {
    return new Promise((resolve) => {
      this.setState(state, resolve)
    });
  }

  render() {
    return this.state.isLoading
      ? this._renderLoader()
      : <NotificationContext.Provider value={this.state}>
        {this.props.children}
      </NotificationContext.Provider>;
  }

  async getMessages(page = 1, forceReload = true, message = null, isError = null) {

    if (forceReload) {
      this.setState({
        isLoading: true
      });
    }

    let messages, totalMessagesCount,errorMessages;

    if (_isEmpty(message) === true || isError === true) {
      const {
        docs,
        total
      } = await messageService.getMessages({
        page,
        paginate: this.state.itemsCountPerPage,
        isAddTimestamp: true,
        excludeFutureScheduled: true,
      });
      const failureMessages = await messageService.getErrorMessages();

      messages = docs;
      errorMessages = failureMessages.docs;
      totalMessagesCount = total;
    } else {
      if (isError === false) {
        message = { ...message, status: 'delivered' };
      }

      messages = [message, ...this.state.messages];
      errorMessages = this.state.errorMessages;
      totalMessagesCount = this.state.totalMessagesCount + 1;
    }

    this.setState({
      activePage: page,
      messages,
      errorMessages,
      isLoading: false,
      totalMessagesCount,
    });
  }

  deleteToken(idx) {
    let {tokens} = {...this.state};
    tokens = tokens.filter( (t, index) => index !== idx);

    const promise = new Promise(
      resolve => {
        this.setState({tokens},
          resolve(true)
        );
    });
    return promise;
  }
  _renderLoader() {
    return (
      <BarLoader
        color={'#3e75c8'}
        height={2}
        width='100%'
        loading={true}
      />
    );
  }

}
